import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import DialPhone from '../../connection/phone';
import { GreenroomState } from '../../stores/types/types';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	COUNTRY_CODE_OPTIONS,
	DEFAULT_COUNTRY_CODE,
	buildE164,
	validateLocalNumber,
} from '../../utils/phone-validation';

type DialStatus = 'idle' | 'dialing' | 'error';

const PhoneDialer: React.FC = () => {
	const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
	const [localNumber, setLocalNumber] = useState('');
	const [status, setStatus] = useState<DialStatus>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const meetingId = useSelector(
		(state: GreenroomState) => state.SessionReducer.meeting?.meeting_id,
	);

	const callerName = user
		? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
		: '';

	const validation = useMemo(() => validateLocalNumber(localNumber, countryCode), [localNumber, countryCode]);

	const handleDial = async () => {
		if (!validation.valid || !token || !meetingId) return;

		setStatus('dialing');
		setErrorMessage('');

		const result = await DialPhone(token, buildE164(countryCode, localNumber), meetingId);

		if (result?.message) {
			setStatus('error');
			setErrorMessage(result.message);
		} else {
			setStatus('idle');
			setLocalNumber('');
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') handleDial();
	};

	return (
		<div className="phone-dialer">
			<div className="phone-dialer__header">
				<Icon name={ICONS.PHONE} size={14} color={COLORS.WHITE} />
				<span className="phone-dialer__label">Dial Phone</span>
				{callerName && (
					<span className="phone-dialer__caller" title={callerName}>
						{callerName}
					</span>
				)}
			</div>
			<select
				className="phone-dialer__country-select"
				value={countryCode}
				onChange={(e) => setCountryCode(e.target.value)}
				disabled={status === 'dialing'}
			>
				{COUNTRY_CODE_OPTIONS.map((entry) => (
					<option key={`${entry.code}-${entry.country}`} value={entry.code}>
						{entry.country} (+{entry.code})
					</option>
				))}
			</select>
			<div className="phone-dialer__row">
				<input
					className="phone-dialer__input dark-input-container"
					type="tel"
					placeholder="555 000 0000"
					value={localNumber}
					onChange={(e) => setLocalNumber(e.target.value)}
					onKeyDown={handleKeyDown}
					disabled={status === 'dialing'}
				/>
				<button
					className="phone-dialer__button"
					onClick={handleDial}
					disabled={status === 'dialing' || !validation.valid}
				>
					{status === 'dialing' ? 'Dialing…' : 'Call'}
				</button>
			</div>
			{localNumber && !validation.valid && validation.error && (
				<span className="phone-dialer__validation-error">{validation.error}</span>
			)}
			{status === 'error' && (
				<span className="phone-dialer__error">{errorMessage}</span>
			)}
		</div>
	);
};

export default PhoneDialer;
